import React, { useState, useMemo } from 'react';
import { useChartContainerWidth } from '../../../../hooks/useChartContainerWidth';
import './DailyTrendChartFat.css';

const DailyTrendChartFat = ({ measurements }) => {
    // Calculate daily averages for chart
    const chartData = useMemo(() => {
        if (!measurements || measurements.length === 0) return { dates: [], fatData: [], measurementDetails: {} };

        // Group measurements by date and store details for tooltips
        const dailyData = {};

        measurements.forEach(m => {
            const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0];
            const time = m.measurement_time ||
                m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                m.created_at?.split('T')[1]?.slice(0, 5) ||
                '';

            if (!dailyData[date]) {
                dailyData[date] = { fat: [], measurements: [] };
            }

            const rawFat = m.fat ?? m.body_fat;
            if (rawFat != null) {
                const val = parseFloat(rawFat);
                if (!isNaN(val)) {
                    dailyData[date].fat.push(val);
                    dailyData[date].measurements.push({
                        date,
                        time,
                        fat: val,
                    });
                }
            }
        });

        // Calculate averages and sort by date
        const sortedDates = Object.keys(dailyData).sort((a, b) => new Date(a) - new Date(b));
        const fatData = sortedDates.map(date => {
            const values = dailyData[date].fat;
            return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
        });

        return { dates: sortedDates, fatData, measurementDetails: dailyData };
    }, [measurements]);

    // Determine dynamic Y-axis min/max with some padding
    const yDomain = useMemo(() => {
        const validFats = chartData.fatData.filter(v => v != null);
        if (validFats.length === 0) return { min: 10, max: 40, step: 5 }; // Safe defaults if no data

        let rawMin = Math.min(...validFats);
        let rawMax = Math.max(...validFats);

        if (rawMin === rawMax) {
            rawMin -= 1;
            rawMax += 1;
        }

        const padding = (rawMax - rawMin) * 0.2; // 20% padding
        const min = Math.max(0, Math.floor(rawMin - padding));
        let max = Math.ceil(rawMax + padding);

        // Calculate a nice step size
        const range = max - min;
        let step = range > 10 ? Math.ceil(range / 5) : (range > 5 ? 2 : (range <= 2 ? 0.25 : 1));
        max = min + (step * 5); // 5 grid sections

        return { min, max, step };
    }, [chartData]);


    // Chart dimensions and scaling
    const [chartGridRef, chartWidth] = useChartContainerWidth(480, 800);
    const chartHeight = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    // Scale functions
    const scaleX = (index) => {
        if (chartData.dates.length <= 1) return padding.left + (plotWidth / 2); // Center if only 1
        return padding.left + (index / (chartData.dates.length - 1)) * plotWidth;
    };

    const scaleY = (value) => {
        if (value == null) return null;
        return padding.top + plotHeight - ((value - yDomain.min) / (yDomain.max - yDomain.min)) * plotHeight;
    };

    // Generate SVG path for line
    const generatePath = (data) => {
        const points = data
            .map((value, index) => {
                const y = scaleY(value);
                if (y == null) return null;
                return { x: scaleX(index), y };
            })
            .filter(p => p != null);

        if (points.length === 0) return '';

        let path = `M ${points[0].x} ${points[0].y}`;
        for (let i = 1; i < points.length; i++) {
            path += ` L ${points[i].x} ${points[i].y}`;
        }
        return path;
    };

    const formatChartDate = (dateStr) => {
        const date = new Date(dateStr);
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };

    const yTicks = Array.from({ length: 6 }, (_, i) => yDomain.min + (yDomain.step * i)).reverse();
    const [tooltip, setTooltip] = useState(null);

    return (
        <div className="dtcf-chart-section">
            <h4 className="dtcf-title">Daily Trend Chart of Fat</h4>

            <div className="dtcf-chart-container">
                <div className="dtcf-chart-title-center">Daily trend chart of Fat</div>
                <div className="dtcf-chart-legend">
                    <div className="dtcf-legend-item">
                        <div className="dtcf-legend-line dtcf-legend-fat"></div>
                        <span>weight</span> {/* Keeping the exact label from mockup */}
                    </div>
                </div>

                <div className="dtcf-chart-area">
                    {chartData.dates.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
                    ) : (
                        <div className="dtcf-chart-placeholder">
                            <div className="dtcf-y-axis">
                                {yTicks.map(tick => (
                                    <span key={tick}>{tick.toFixed(2)}</span>
                                ))}
                            </div>
                            <div className="dtcf-chart-grid" ref={chartGridRef}>
                                <svg
                                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                    preserveAspectRatio="xMidYMid meet"
                                    className="dtcf-chart-svg"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                >
                                    {yTicks.map(value => {
                                        const y = scaleY(value);
                                        if (y == null) return null;
                                        return (
                                            <line
                                                key={`grid-${value}`}
                                                x1={padding.left}
                                                y1={y}
                                                x2={chartWidth - padding.right}
                                                y2={y}
                                                stroke="#efefef"
                                                strokeWidth="1"
                                            />
                                        );
                                    })}

                                    {chartData.fatData.some(v => v != null) && (
                                        <path
                                            d={generatePath(chartData.fatData)}
                                            fill="none"
                                            stroke="#4B8B9B"
                                            strokeWidth="2"
                                        />
                                    )}

                                    {chartData.dates.map((date, index) => {
                                        const fatValue = chartData.fatData[index];
                                        const x = scaleX(index);
                                        if (fatValue == null) return null;

                                        const handleMouseEnter = (e) => {
                                            const rect = e.currentTarget.closest('.dtcf-chart-grid')?.getBoundingClientRect();
                                            if (!rect) return;
                                            setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, date, fatValue });
                                        };
                                        const handleMouseLeave = () => setTooltip(null);

                                        return (
                                            <g key={`points-${date}`}>
                                                <line
                                                    x1={x}
                                                    y1={padding.top}
                                                    x2={x}
                                                    y2={chartHeight - padding.bottom}
                                                    stroke="transparent"
                                                    strokeWidth="20"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={handleMouseEnter}
                                                    onMouseLeave={handleMouseLeave}
                                                />
                                            </g>
                                        );
                                    })}
                                </svg>

                                <div className="dtcf-x-axis">
                                    {chartData.dates.map((date, index) => {
                                        const skip = Math.ceil(chartData.dates.length / 5) || 1;
                                        if (index % skip === 0 || index === chartData.dates.length - 1) {
                                            return <span key={date} style={{ left: `${(scaleX(index) / chartWidth) * 100}%` }}>{formatChartDate(date)}</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                {tooltip && (
                                    <div
                                        className="dtcf-tooltip"
                                        style={{ left: `${tooltip.x}px`, top: `${tooltip.y - 40}px`, transform: 'translateX(-50%)' }}
                                    >
                                        <div className="dtcf-tooltip-content">
                                            <div className="dtcf-tooltip-date">{formatChartDate(tooltip.date)}</div>
                                            <div className="dtcf-tooltip-item">
                                                <span className="dtcf-tooltip-value">{tooltip.fatValue.toFixed(2)}%</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyTrendChartFat;
